import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Landmark, Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Minus, Download, Eye, Loader2, ArrowRight, MessageCircle, Mail, Search, X,
} from "lucide-react";
import { Input } from "../components/ui/input";

const ESTADOS_MOV = {
  matched:   { label: "Conciliado", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2, dot: "bg-emerald-500" },
  no_match:  { label: "Sin coincidencia", color: "bg-orange-100 text-orange-800 border-orange-200", icon: AlertTriangle, dot: "bg-orange-500" },
  duplicado: { label: "Duplicado", color: "bg-amber-100 text-amber-800 border-amber-200", icon: XCircle, dot: "bg-amber-500" },
  debito:    { label: "Débito (ignorado)", color: "bg-slate-100 text-slate-600 border-slate-200", icon: Minus, dot: "bg-slate-400" },
};

const fmtMoney = (n) => `Bs. ${(Number(n) || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function EstadoBadge({ estado }) {
  const meta = ESTADOS_MOV[estado] || ESTADOS_MOV.no_match;
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.color} border hover:opacity-100`}>
      <Icon className="w-3 h-3 mr-1" />{meta.label}
    </Badge>
  );
}

export default function ConciliacionBancariaPage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState("subir");
  const [uploading, setUploading] = useState(false);
  const [ultimoLote, setUltimoLote] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [detalleLote, setDetalleLote] = useState(null);

  const loadLotes = () => axios.get(`${API}/conciliacion/lotes`, auth).then(r => setLotes(r.data)).catch(() => {});
  useEffect(() => { if (tab === "historial") loadLotes(); }, [tab]); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permitir subir el mismo archivo dos veces
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("El archivo excede 15 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/conciliacion/upload`, fd, auth);
      const lote = r.data?.lote;
      setUltimoLote(lote);
      const s = lote?.stats || {};
      toast.success(`Conciliado · ${s.matched || 0} validado(s) · ${s.no_match || 0} sin match · ${s.duplicado || 0} duplicado(s)`);
      setTab("resultado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar el archivo");
    } finally { setUploading(false); }
  };

  const descargarOriginal = async (lote) => {
    try {
      const r = await axios.get(`${API}/conciliacion/lotes/${lote.id}/archivo`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = lote.archivo_original || "archivo";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el archivo"); }
  };

  const verDetalle = async (loteId) => {
    try {
      const r = await axios.get(`${API}/conciliacion/lotes/${loteId}`, auth);
      setDetalleLote(r.data);
    } catch { toast.error("No se pudo cargar el detalle"); }
  };

  return (
    <div className="space-y-6" data-testid="conciliacion-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
          <Landmark className="w-8 h-8 text-orange-500" /> Conciliación Bancaria
        </h1>
        <p className="text-green-700/70 mt-1">
          Suba el extracto de su cuenta bancaria (PDF, CSV o Excel). El sistema comparará automáticamente los movimientos
          con los pagos pendientes por validar y los conciliará usando referencia + monto + fecha (± 1 día).
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="subir" data-testid="tab-conc-subir">Subir archivo</TabsTrigger>
          <TabsTrigger value="resultado" data-testid="tab-conc-resultado" disabled={!ultimoLote}>Último resultado</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-conc-historial">Historial</TabsTrigger>
        </TabsList>

        {/* ===== SUBIR ===== */}
        <TabsContent value="subir" className="mt-4">
          <Card className="p-8 border-green-100">
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              {/* Dropzone */}
              <label
                htmlFor="file-conc"
                className={`block cursor-pointer border-2 border-dashed rounded-2xl p-10 text-center transition ${
                  uploading ? "border-orange-300 bg-orange-50/60" : "border-green-300 bg-green-50/40 hover:border-orange-400 hover:bg-orange-50/40"
                }`}
                data-testid="dropzone-conciliacion"
              >
                <input
                  id="file-conc"
                  type="file"
                  className="hidden"
                  accept=".pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={onFile}
                  disabled={uploading}
                  data-testid="input-file-conciliacion"
                />
                {uploading ? (
                  <>
                    <Loader2 className="w-14 h-14 text-orange-500 mx-auto mb-4 animate-spin" />
                    <p className="font-display font-bold text-lg text-orange-800">Procesando movimientos…</p>
                    <p className="text-sm text-orange-700/80 mt-1">Un momento, estamos conciliando con los pagos pendientes.</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-green-800 to-green-900 flex items-center justify-center mb-4 shadow-lg">
                      <Upload className="w-9 h-9 text-orange-300" strokeWidth={1.7} />
                    </div>
                    <p className="font-display font-extrabold text-xl text-green-900">Suba su extracto bancario</p>
                    <p className="text-sm text-green-800/70 mt-2 max-w-md mx-auto">
                      Arrastre o haga clic para seleccionar. Formatos aceptados: <strong>PDF, CSV, XLSX</strong> · máximo 15 MB.
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 text-white font-semibold text-sm">
                      <Upload className="w-4 h-4" /> Elegir archivo
                    </div>
                  </>
                )}
              </label>

              {/* Info */}
              <div className="space-y-3 text-sm">
                <h3 className="font-display font-bold text-green-900 flex items-center gap-2 text-lg">
                  <FileSpreadsheet className="w-5 h-5 text-orange-500" /> ¿Qué debe contener el archivo?
                </h3>
                <ul className="space-y-2 text-green-800/85">
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> Columnas: <strong>Referencia</strong>, <strong>Fecha</strong>, <strong>Descripción</strong> y <strong>Monto</strong>.</li>
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> Los <strong>débitos</strong> (montos negativos) son ignorados automáticamente.</li>
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> Un pago se valida si coinciden los <strong>últimos 6 dígitos</strong> de la referencia + monto exacto + fecha ± 1 día.</li>
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> Las referencias ya validadas anteriormente se marcan como <strong>Duplicado</strong> para evitar doble conteo.</li>
                </ul>
                <p className="text-xs text-slate-500 mt-4 border-t border-green-100 pt-3">
                  💡 Recomendación: descargue directamente el extracto del portal de su banco. Compatible con formatos de BFC, Banesco, BDV, Mercantil.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ===== RESULTADO ===== */}
        <TabsContent value="resultado" className="mt-4">
          {ultimoLote && (
            <div className="space-y-4">
              <ResumenLote lote={ultimoLote} onDescargar={() => descargarOriginal(ultimoLote)} />
              <TablaDetalle detalle={ultimoLote.detalle || []} />
            </div>
          )}
        </TabsContent>

        {/* ===== HISTORIAL ===== */}
        <TabsContent value="historial" className="mt-4">
          <Card className="p-4 border-green-100">
            <h3 className="font-display font-bold text-lg text-green-900 mb-3">Lotes de conciliación</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900">
                  <tr>
                    <th className="text-left p-2.5">Fecha</th>
                    <th className="text-left p-2.5">Archivo</th>
                    <th className="text-left p-2.5">Banco</th>
                    <th className="text-center p-2.5">Total</th>
                    <th className="text-center p-2.5">Conciliados</th>
                    <th className="text-center p-2.5">Sin match</th>
                    <th className="text-center p-2.5">Duplicados</th>
                    <th className="text-left p-2.5">Usuario</th>
                    <th className="text-right p-2.5">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lotes.map(l => (
                    <tr key={l.id} className="border-b border-green-50" data-testid={`lote-row-${l.id}`}>
                      <td className="p-2.5 text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2.5 truncate max-w-xs">{l.archivo_original}</td>
                      <td className="p-2.5">{l.banco_detectado || "-"}</td>
                      <td className="p-2.5 text-center font-mono">{l.stats?.total || 0}</td>
                      <td className="p-2.5 text-center text-emerald-700 font-bold">{l.stats?.matched || 0}</td>
                      <td className="p-2.5 text-center text-orange-700">{l.stats?.no_match || 0}</td>
                      <td className="p-2.5 text-center text-amber-700">{l.stats?.duplicado || 0}</td>
                      <td className="p-2.5 text-xs">{l.creado_por_email}</td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => verDetalle(l.id)} title="Ver detalle" data-testid={`ver-${l.id}`}>
                          <Eye className="w-4 h-4 text-green-700" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => descargarOriginal(l)} title="Descargar original" data-testid={`down-${l.id}`}>
                          <Download className="w-4 h-4 text-orange-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!lotes.length && (
                    <tr><td colSpan="9" className="p-8 text-center text-slate-400">Aún no hay lotes de conciliación</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal detalle */}
      <Dialog open={!!detalleLote} onOpenChange={o => !o && setDetalleLote(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del lote de conciliación</DialogTitle>
            <DialogDescription>
              {detalleLote?.archivo_original} · {detalleLote?.banco_detectado} · {(detalleLote?.created_at || "").slice(0, 16).replace("T", " ")}
            </DialogDescription>
          </DialogHeader>
          {detalleLote && (
            <div className="space-y-4">
              <ResumenLote lote={detalleLote} onDescargar={() => descargarOriginal(detalleLote)} />
              <TablaDetalle detalle={detalleLote.detalle || []} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResumenLote({ lote, onDescargar }) {
  const s = lote.stats || {};
  const kpis = [
    { k: "total", label: "Total movimientos", val: s.total || 0, color: "text-green-900", bg: "bg-green-50", ring: "ring-green-200" },
    { k: "matched", label: "Conciliados", val: s.matched || 0, color: "text-emerald-800", bg: "bg-emerald-50", ring: "ring-emerald-200" },
    { k: "no_match", label: "Sin match", val: s.no_match || 0, color: "text-orange-800", bg: "bg-orange-50", ring: "ring-orange-200" },
    { k: "duplicado", label: "Duplicados", val: s.duplicado || 0, color: "text-amber-800", bg: "bg-amber-50", ring: "ring-amber-200" },
    { k: "debito", label: "Débitos (ignorados)", val: s.debito || 0, color: "text-slate-700", bg: "bg-slate-100", ring: "ring-slate-200" },
  ];
  return (
    <Card className="p-5 border-green-100" data-testid="lote-resumen">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600">Lote de conciliación</p>
          <h3 className="font-display font-extrabold text-xl text-green-900">{lote.banco_detectado || "Extracto bancario"}</h3>
          <p className="text-sm text-slate-600 mt-0.5">{lote.archivo_original}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onDescargar} data-testid="btn-desc-original">
          <Download className="w-4 h-4 mr-2" /> Descargar original
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map(k => (
          <div key={k.k} className={`p-4 rounded-xl ${k.bg} ring-1 ${k.ring}`} data-testid={`kpi-${k.k}`}>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{k.label}</p>
            <p className={`text-3xl font-display font-extrabold mt-1 ${k.color}`}>{k.val}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TablaDetalle({ detalle }) {
  const abrirWA = (m) => {
    if (m.whatsapp_url) window.open(m.whatsapp_url, "_blank", "noopener");
  };
  const emailBadge = (est) => {
    if (est === "enviado") return <span title="Email enviado al contribuyente" className="inline-flex items-center gap-1 text-xs text-emerald-700"><Mail className="w-3.5 h-3.5" />Enviado</span>;
    if (est === "sin_email") return <span title="El contribuyente no tiene email registrado" className="inline-flex items-center gap-1 text-xs text-slate-500"><Mail className="w-3.5 h-3.5" />Sin email</span>;
    if (est === "sin_api_key") return <span title="Resend API key no configurada" className="inline-flex items-center gap-1 text-xs text-amber-700"><Mail className="w-3.5 h-3.5" />Pendiente</span>;
    if (est === "ya_enviado") return <span title="Ya se había enviado antes" className="inline-flex items-center gap-1 text-xs text-slate-500"><Mail className="w-3.5 h-3.5" />Ya enviado</span>;
    if (est === "error") return <span title="Error al enviar email" className="inline-flex items-center gap-1 text-xs text-rose-700"><Mail className="w-3.5 h-3.5" />Error</span>;
    return null;
  };

  // ============ Multi-filtros por columna ============
  const [filtros, setFiltros] = useState({
    descripcion: "",
    referencia: "",
    fecha: "",
    monto: "",
    nro_comprobante: "",
  });
  const setF = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));
  const clearAll = () => {
    setFiltros({ descripcion: "", referencia: "", fecha: "", monto: "", nro_comprobante: "" });
    setEstadoFiltro("todos");
  };
  const anyTextFilter = Object.values(filtros).some(v => (v || "").trim() !== "");

  // Chip de filtro rápido por estado del movimiento
  const [estadoFiltro, setEstadoFiltro] = useState("todos"); // 'todos' | 'matched' | 'no_match' | 'duplicado' | 'debito'
  const anyFilter = anyTextFilter || estadoFiltro !== "todos";

  // Conteos por estado (siempre sobre la data cruda, sin filtros de texto)
  const conteoEstados = useMemo(() => {
    const acc = { matched: 0, no_match: 0, duplicado: 0, debito: 0 };
    for (const m of detalle) {
      if (acc[m.estado] !== undefined) acc[m.estado] += 1;
    }
    return acc;
  }, [detalle]);

  const norm = (s) => String(s ?? "").toLowerCase();
  const detalleFiltrado = useMemo(() => {
    if (!anyFilter) return detalle;
    return detalle.filter((m) => {
      if (estadoFiltro !== "todos" && m.estado !== estadoFiltro) return false;
      if (filtros.descripcion && !norm(m.descripcion).includes(norm(filtros.descripcion))) return false;
      if (filtros.referencia && !(norm(m.referencia).includes(norm(filtros.referencia)) || norm(m.ref_last6).includes(norm(filtros.referencia)))) return false;
      if (filtros.fecha && !norm(m.fecha).includes(norm(filtros.fecha))) return false;
      if (filtros.monto) {
        const raw = String(m.monto ?? "");
        const fmt = fmtMoney(m.monto);
        const q = filtros.monto.replace(/\s/g, "");
        if (!raw.includes(q) && !fmt.toLowerCase().includes(q.toLowerCase())) return false;
      }
      if (filtros.nro_comprobante) {
        const nc = m.nro_comprobante || m.nro_comprobante_duplicado || "";
        if (!norm(nc).includes(norm(filtros.nro_comprobante))) return false;
      }
      return true;
    });
  }, [detalle, filtros, anyFilter, estadoFiltro]);

  // Helper inline (no nested component) para renderizar un input de filtro por columna.
  // Definido como función pura que devuelve JSX — no crea un componente React nuevo por render.
  const renderFilterInput = (column, placeholder, extraCls = "") => (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      <Input
        value={filtros[column]}
        onChange={(e) => setF(column, e.target.value)}
        placeholder={placeholder}
        className={`h-7 pl-6 pr-2 text-xs bg-white ${extraCls}`}
        data-testid={`concil-detalle-filter-${column}`}
      />
    </div>
  );

  return (
    <Card className="p-4 border-green-100">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-display font-bold text-lg text-green-900">Detalle de movimientos</h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-600" data-testid="concil-detalle-count">
            {anyFilter
              ? <><strong>{detalleFiltrado.length}</strong> de <strong>{detalle.length}</strong></>
              : <><strong>{detalle.length}</strong> movimiento{detalle.length === 1 ? "" : "s"}</>}
          </span>
          {anyFilter && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearAll}
              className="h-7 px-2 text-slate-600 hover:text-red-600"
              data-testid="concil-detalle-clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros
            </Button>
          )}
        </div>
      </div>
      {/* Chips de filtro rápido por estado */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="concil-detalle-chips">
        {[
          { key: "todos",     label: "Todos",             count: detalle.length,             cls: "bg-green-100 text-green-800 border-green-200",       activeCls: "bg-green-700 text-white border-green-800" },
          { key: "matched",   label: "Conciliados",       count: conteoEstados.matched,      cls: "bg-emerald-50 text-emerald-800 border-emerald-200",  activeCls: "bg-emerald-600 text-white border-emerald-700" },
          { key: "no_match",  label: "Sin coincidencia",  count: conteoEstados.no_match,     cls: "bg-orange-50 text-orange-800 border-orange-200",     activeCls: "bg-orange-600 text-white border-orange-700" },
          { key: "duplicado", label: "Duplicados",        count: conteoEstados.duplicado,    cls: "bg-amber-50 text-amber-800 border-amber-200",        activeCls: "bg-amber-500 text-white border-amber-600" },
          { key: "debito",    label: "Débitos",           count: conteoEstados.debito,       cls: "bg-slate-100 text-slate-700 border-slate-200",       activeCls: "bg-slate-700 text-white border-slate-800" },
        ].map((chip) => {
          const active = estadoFiltro === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setEstadoFiltro(chip.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium transition-all ${active ? chip.activeCls + " shadow-sm" : chip.cls + " hover:opacity-80"}`}
              data-testid={`concil-detalle-chip-${chip.key}`}
            >
              {chip.label}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-white/20" : "bg-white/70"}`}>
                {chip.count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900">
            <tr>
              <th className="text-left p-2.5">Estado</th>
              <th className="text-left p-2.5">Referencia</th>
              <th className="text-left p-2.5">Últ. 6</th>
              <th className="text-left p-2.5">Fecha</th>
              <th className="text-right p-2.5">Monto</th>
              <th className="text-left p-2.5">Descripción</th>
              <th className="text-left p-2.5">Nº Comprobante</th>
              <th className="text-center p-2.5">Notificación</th>
            </tr>
            <tr className="bg-white border-b border-green-100">
              <th className="p-1.5"></th>
              <th className="p-1.5">{renderFilterInput("referencia", "Filtrar…")}</th>
              <th className="p-1.5"></th>
              <th className="p-1.5">{renderFilterInput("fecha", "YYYY-MM-DD")}</th>
              <th className="p-1.5">{renderFilterInput("monto", "Bs / número", "text-right")}</th>
              <th className="p-1.5">{renderFilterInput("descripcion", "Filtrar detalles…")}</th>
              <th className="p-1.5">{renderFilterInput("nro_comprobante", "Nº comprobante")}</th>
              <th className="p-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {detalleFiltrado.map((m, i) => (
              <tr key={i} className="border-b border-green-50" data-testid={`mov-row-${i}`}>
                <td className="p-2.5"><EstadoBadge estado={m.estado} /></td>
                <td className="p-2.5 font-mono text-xs">{m.referencia}</td>
                <td className="p-2.5 font-mono text-xs font-bold text-orange-700">{m.ref_last6}</td>
                <td className="p-2.5">{m.fecha}</td>
                <td className="p-2.5 text-right font-semibold">{fmtMoney(m.monto)}</td>
                <td className="p-2.5 text-xs text-slate-600 truncate max-w-xs" title={m.descripcion}>{m.descripcion}</td>
                <td className="p-2.5 font-mono text-xs">
                  {m.nro_comprobante || m.nro_comprobante_duplicado || "-"}
                </td>
                <td className="p-2.5 text-center">
                  {m.estado === "matched" ? (
                    <div className="flex items-center justify-center gap-2">
                      {emailBadge(m.email_status)}
                      {m.whatsapp_url && (
                        <button
                          onClick={() => abrirWA(m)}
                          title={m.whatsapp_phone ? `Enviar WhatsApp a +${m.whatsapp_phone}` : "Compartir por WhatsApp"}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition"
                          data-testid={`btn-wa-${i}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                      )}
                    </div>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
              </tr>
            ))}
            {!detalleFiltrado.length && (
              <tr><td colSpan="8" className="p-8 text-center text-slate-400">
                {detalle.length ? "Ningún movimiento coincide con los filtros" : "Sin movimientos"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
